import React from 'react';

import {
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';


type CalendarDay = {
  day: number;
  dateKey: string;
  isCurrentMonth: boolean;
};


type CalendarTournament = {
  id: number;
  title: string;
  startTime: string;
};


type TournamentCalendarProps = {
  currentYear: number;

  currentMonth: number;

  todayKey: string;

  selectedDate: string;

  calendarDays:
    CalendarDay[];

  tournamentMap:
    Map<
      string,
      CalendarTournament[]
    >;

  onPreviousMonth:
    () => void;

  onNextMonth:
    () => void;

  onDateClick: (
    dateKey: string,
  ) => void;
};


const WEEK_DAYS = [
  '일',
  '월',
  '화',
  '수',
  '목',
  '금',
  '토',
];


const TournamentCalendar:
  React.FC<
    TournamentCalendarProps
  > = ({
    currentYear,
    currentMonth,

    todayKey,
    selectedDate,

    calendarDays,
    tournamentMap,

    onPreviousMonth,
    onNextMonth,
    onDateClick,
  }) => {
    return (
      <section className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm sm:rounded-2xl">

        {/* =================================================
            월 이동 Header
            ================================================= */}
        <div className="flex h-11 shrink-0 items-center justify-between border-b border-gray-200 px-2 sm:h-14 sm:px-4">

          <button
            type="button"
            onClick={
              onPreviousMonth
            }
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-600 transition hover:bg-gray-100 sm:h-9 sm:w-9"
            aria-label="이전 달"
          >
            <ChevronLeft
              size={22}
            />
          </button>


          <h2 className="text-base font-bold text-gray-900 sm:text-xl">
            {currentYear}년{' '}
            {currentMonth + 1}월
          </h2>


          <button
            type="button"
            onClick={
              onNextMonth
            }
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-600 transition hover:bg-gray-100 sm:h-9 sm:w-9"
            aria-label="다음 달"
          >
            <ChevronRight
              size={22}
            />
          </button>

        </div>


        {/* =================================================
            요일
            ================================================= */}
        <div className="grid h-8 shrink-0 grid-cols-7 border-b border-gray-200 bg-gray-50 sm:h-10">

          {WEEK_DAYS.map(
            (
              weekDay,
              index,
            ) => (
              <div
                key={
                  weekDay
                }
                className={`flex items-center justify-center text-[11px] font-semibold sm:text-sm ${
                  index === 0
                    ? 'text-red-500'
                    : index === 6
                      ? 'text-blue-500'
                      : 'text-gray-600'
                }`}
              >
                {
                  weekDay
                }
              </div>
            ),
          )}

        </div>


        {/* =================================================
            달력
            ================================================= */}
        <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6">

          {calendarDays.map(
            (
              calendarDay,
              index,
            ) => {
              const dateTournaments =
                tournamentMap.get(
                  calendarDay
                    .dateKey,
                ) ?? [];


              /**
               * 오늘 이전 날짜만 과거
               *
               * 오늘은 클릭 제한 가능하지만
               * 대회는 표시합니다.
               */
              const isPast =
                calendarDay
                  .dateKey <
                todayKey;


              const isToday =
                calendarDay
                  .dateKey ===
                todayKey;


              /**
               * 정책:
               * 오늘 포함 과거는 선택 불가
               */
              const isDisabled =
                calendarDay
                  .dateKey <=
                todayKey;


              const isSelected =
                calendarDay
                  .dateKey ===
                selectedDate;


              const weekDay =
                index % 7;


              const hasTournament =
                dateTournaments.length >
                0;


              return (
                <button
                  key={
                    calendarDay
                      .dateKey
                  }
                  type="button"
                  disabled={
                    isDisabled
                  }
                  onClick={() =>
                    onDateClick(
                      calendarDay
                        .dateKey,
                    )
                  }
                  className={[
                    'relative min-h-0 overflow-hidden border-b border-r border-gray-100 p-1 text-left transition sm:p-1.5 lg:p-2',

                    isPast
                      ? 'cursor-not-allowed bg-gray-100/80 text-gray-300 opacity-60'
                      : isToday
                        ? 'cursor-not-allowed bg-amber-50/50 text-gray-700'
                        : !calendarDay
                              .isCurrentMonth
                          ? 'bg-gray-50/70 text-gray-400 hover:bg-gray-100'
                          : 'bg-white hover:bg-gray-50',

                    !isDisabled &&
                    isSelected
                      ? 'z-[1] ring-2 ring-inset ring-main'
                      : '',
                  ].join(
                    ' ',
                  )}
                >

                  {/* =========================================
                      날짜
                      ========================================= */}
                  <div className="flex items-start justify-between gap-1">

                    <div className="flex items-center gap-1">

                      <span
                        className={`text-xs font-semibold ${
                          isToday
                            ? 'flex h-5 w-5 items-center justify-center rounded-full bg-main text-white'
                            : !isPast &&
                                weekDay ===
                                  0
                              ? 'text-red-500'
                              : !isPast &&
                                  weekDay ===
                                    6
                                ? 'text-blue-500'
                                : ''
                        }`}
                      >
                        {
                          calendarDay.day
                        }
                      </span>


                      {isToday && (
                        <span className="hidden text-[8px] font-bold text-main lg:inline">
                          TODAY
                        </span>
                      )}

                    </div>


                    {dateTournaments
                      .length >
                      1 && (
                      <span className="rounded-full bg-gray-900 px-1.5 py-0.5 text-[9px] font-bold leading-none text-white">
                        {
                          dateTournaments.length
                        }
                      </span>
                    )}

                  </div>


                  {/* =========================================
                      모바일 대회 표시

                      기존 코드는 sm:block이라
                      모바일에서는 아무 표시도 안 났음.

                      모바일에서는 작은 점으로 표시합니다.
                      ========================================= */}
                  {hasTournament &&
                    !isPast && (
                      <div className="mt-1 flex gap-1 sm:hidden">

                        {dateTournaments
                          .slice(
                            0,
                            3,
                          )
                          .map(
                            (
                              tournament,
                            ) => (
                              <span
                                key={
                                  tournament.id
                                }
                                className="h-1.5 w-1.5 rounded-full bg-orange-500"
                              />
                            ),
                          )}

                      </div>
                    )}


                  {/* =========================================
                      태블릿 / PC 대회 정보

                      오늘도 표시되도록
                      !isDisabled 조건 제거
                      ========================================= */}
                  {hasTournament &&
                    !isPast && (

                    <div className="mt-1 hidden space-y-0.5 sm:block">

                      {dateTournaments
                        .slice(
                          0,
                          2,
                        )
                        .map(
                          (
                            tournament,
                          ) => (

                            <div
                              key={
                                tournament.id
                              }
                              className="truncate rounded bg-orange-50 px-1 py-0.5 text-[9px] font-medium text-orange-700 lg:text-[10px]"
                              title={`${tournament.startTime} ${tournament.title}`}
                            >
                              {
                                tournament.startTime
                              }{' '}
                              {
                                tournament.title
                              }
                            </div>

                          ),
                        )}


                      {dateTournaments
                        .length >
                        2 && (
                        <div className="px-1 text-[8px] font-semibold text-gray-400">
                          +
                          {dateTournaments
                            .length -
                            2}
                          개 더보기
                        </div>
                      )}

                    </div>

                  )}

                </button>
              );
            },
          )}

        </div>

      </section>
    );
  };


export default TournamentCalendar;